import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import type { SessionUser } from "@icraftx/shared/v1";
import { logout } from "@/shared/api/auth";
import { PersonAvatar } from "@/modules/shell/PersonAvatar";
import { DeskLookSection } from "@/modules/shell/DeskLookSection";

function accountLabel(user: SessionUser): string {
  const name = user.displayName?.trim();
  if (name) return name;
  const email = user.email?.trim();
  if (email) return email;
  return "Account";
}

/**
 * Top-right avatar menu — Look · Switch company · Sign out (plan/35 · plan/50).
 */
export function DeskAccountMenu(props: { user: SessionUser }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const label = accountLabel(props.user);
  const email = props.user.email?.trim() || null;

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = 280;
      const left = Math.min(
        Math.max(8, rect.right - width),
        window.innerWidth - width - 8,
      );
      setPos({ top: rect.bottom + 6, left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex size-9 items-center justify-center rounded-full border-0 bg-transparent p-0 text-[var(--color-muted)] transition-colors hover:bg-[var(--color-border)]/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Account menu — ${label}`}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        <PersonAvatar label={label} size="md" variant="solid" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              aria-labelledby={titleId}
              className="fixed z-[80] w-[min(100vw-1rem,17.5rem)] rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-card)] p-1.5 shadow-[var(--shadow-float)] animate-[fadeIn_160ms_ease-out]"
              style={{ top: pos.top, left: pos.left }}
            >
              <div className="border-b border-[var(--color-border)]/70 px-2.5 py-2.5">
                <p
                  id={titleId}
                  className="m-0 truncate text-sm font-semibold text-[var(--color-foreground)]"
                >
                  {label}
                </p>
                {email && email !== label ? (
                  <p className="m-0 mt-0.5 truncate text-xs text-[var(--color-muted)]">
                    {email}
                  </p>
                ) : null}
              </div>

              <div className="border-b border-[var(--color-border)]/70 px-1.5 py-2.5">
                <DeskLookSection />
              </div>

              <div className="py-1">
                <Link
                  role="menuitem"
                  to="/select-company"
                  className="flex w-full items-center rounded-[var(--radius-md)] px-2.5 py-2 text-sm text-[var(--color-foreground)] no-underline transition-colors hover:bg-[var(--color-border)]/35"
                  onClick={() => setOpen(false)}
                >
                  Switch company
                </Link>
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center rounded-[var(--radius-md)] border-0 bg-transparent px-2.5 py-2 text-left text-sm text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-border)]/35"
                  onClick={() => {
                    setOpen(false);
                    void logout().then(() => navigate("/login"));
                  }}
                >
                  Sign out
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
